import type { LessonTemplateNotation } from "./lessonTemplates";
import { ann, circle, line } from "./lessonTemplates";
import {
  dataDotPlotFigure,
  dataDotPlotSteps,
} from "./practiceArithmeticFigures";
import type { PracticeBand } from "./practiceBands";
import type { PracticeGeneratorMeta } from "./practiceGeneratorCatalog";
import type { PracticeDraw } from "./practiceGenerators";
import type { PracticeLocale } from "./practiceLocale";
import type { PracticeDifficulty } from "./practiceQuestionTemplates";
import type { PracticeSubject } from "./practiceSubjects";

type Sampler = (rng: () => number, locale: PracticeLocale) => PracticeDraw;

function randInt(rng: () => number, min: number, max: number): number {
  const lo = Math.ceil(min);
  const hi = Math.floor(max);
  return lo + Math.floor(rng() * (hi - lo + 1));
}

function choice<T>(rng: () => number, items: readonly T[]): T {
  return items[randInt(rng, 0, items.length - 1)];
}

function say(locale: PracticeLocale, en: string, he: string): string {
  return locale === "he" ? he : en;
}

function hint(locale: PracticeLocale, en: string, he: string): string[] {
  return [say(locale, en, he)];
}

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

function fraction(
  num: number,
  den: number,
): { expectedAnswer: string; acceptedAnswers: string[] } {
  const g = gcd(num, den);
  const simple = `${num / g}/${den / g}`;
  const raw = `${num}/${den}`;
  const accepted = new Set<string>();
  if (raw !== simple) accepted.add(raw);
  if (den / g === 1) accepted.add(String(num / g));
  return { expectedAnswer: simple, acceptedAnswers: [...accepted] };
}

function row(
  id: string,
  gradeMin: number,
  gradeMax: number,
  difficulty: PracticeDifficulty,
  subject: PracticeSubject,
  band: PracticeBand,
  en: string,
  he: string,
): PracticeGeneratorMeta {
  return {
    id,
    skillId: id,
    gradeMin,
    gradeMax,
    difficulty,
    subject,
    band,
    name: { en, he },
    skillLabel: { en, he },
  };
}

const TRIPLES: ReadonlyArray<readonly [number, number, number]> = [
  [3, 4, 5],
  [5, 12, 13],
  [6, 8, 10],
  [8, 15, 17],
  [7, 24, 25],
  [9, 12, 15],
  [9, 40, 41],
  [20, 21, 29],
];

export const GEOMETRY_STATISTICS_GENERATOR_CATALOG: readonly PracticeGeneratorMeta[] = [
  row("geometry.triangle-area", 5, 6, "intro", "Geometry", "elementary", "Area of a triangle", "שטח של משולש"),
  row("geometry.angle-sum", 5, 6, "intro", "Geometry", "elementary", "Missing angle in a triangle", "זווית חסרה במשולש"),
  row("geometry.pythagorean", 8, 9, "core", "Geometry", "middle", "Find the hypotenuse", "מציאת היתר"),
  row("geometry.circle-circumference", 8, 9, "core", "Geometry", "middle", "Circumference of a circle", "היקף של מעגל"),
  row("geometry.circle-area", 8, 9, "core", "Geometry", "middle", "Area of a circle", "שטח של מעגל"),
  row("geometry.similar-triangles", 10, 11, "core", "Geometry", "high", "Similar triangles", "משולשים דומים"),
  row("stats.mean", 5, 6, "intro", "Statistics", "elementary", "Mean of a data set", "ממוצע של נתונים"),
  row("stats.median", 5, 6, "intro", "Statistics", "elementary", "Median of a data set", "חציון של נתונים"),
  row("stats.mode", 5, 6, "intro", "Statistics", "elementary", "Mode of a data set", "שכיח של נתונים"),
  row("stats.range", 5, 6, "intro", "Statistics", "elementary", "Range of a data set", "טווח של נתונים"),
  row("stats.sample-space", 5, 6, "intro", "Statistics", "elementary", "Probability of a die roll", "הסתברות בהטלת קובייה"),
  row("stats.independent-events", 8, 9, "challenge", "Statistics", "middle", "Independent events", "מאורעות בלתי תלויים"),
];

function triangleFigure(base: number, height: number, locale: PracticeLocale): LessonTemplateNotation[] {
  return [
    line(8, 18, 28, 18),
    line(8, 18, 18, 8),
    line(18, 8, 28, 18),
    ann(14, 19, say(locale, `base = ${base}`, `בסיס = ${base}`)),
    ann(19, 12, say(locale, `h = ${height}`, `גובה = ${height}`)),
  ];
}

function angleFigure(a: number, b: number): LessonTemplateNotation[] {
  return [
    line(8, 18, 28, 18),
    line(8, 18, 18, 6),
    line(18, 6, 28, 18),
    ann(11, 16, `${a}°`),
    ann(23, 16, `${b}°`),
    ann(17, 9, "?"),
  ];
}

function rightTriangleFigure(a: number, b: number): LessonTemplateNotation[] {
  return [
    line(8, 20, 28, 20),
    line(8, 20, 8, 8),
    line(8, 8, 28, 20),
    ann(6, 14, String(a)),
    ann(16, 21, String(b)),
    ann(18, 12, "?"),
  ];
}

function circleFigure(radius: number, locale: PracticeLocale): LessonTemplateNotation[] {
  return [
    circle(18, 14, 6),
    line(18, 14, 24, 14),
    ann(20, 13, say(locale, `r = ${radius}`, `r = ${radius}`)),
  ];
}

function similarFigure(
  small: number,
  big: number,
  known: number,
): LessonTemplateNotation[] {
  return [
    line(6, 20, 16, 20),
    line(6, 20, 11, 12),
    line(11, 12, 16, 20),
    ann(10, 21, String(small)),
    ann(14, 15, String(known)),
    line(22, 20, 42, 20),
    line(22, 20, 32, 8),
    line(32, 8, 42, 20),
    ann(30, 21, String(big)),
    ann(38, 13, "x"),
  ];
}

function dataSet(rng: () => number, count: number, min: number, max: number): number[] {
  return Array.from({ length: count }, () => randInt(rng, min, max));
}

export const GEOMETRY_STATISTICS_SAMPLERS: Record<string, Sampler> = {
  "geometry.triangle-area"(rng, locale) {
    const base = randInt(rng, 2, 8) * 2;
    const height = randInt(rng, 2, 10);
    const area = (base * height) / 2;
    return {
      prompt: say(
        locale,
        `Find the area of the triangle.\nbase = ${base}\nheight = ${height}`,
        `מצאו את שטח המשולש.\nבסיס = ${base}\nגובה = ${height}`,
      ),
      expectedAnswer: String(area),
      acceptedAnswers: [`${area} square units`],
      hints: hint(
        locale,
        "The area of a triangle is half the base times the height.",
        "שטח משולש הוא חצי הבסיס כפול הגובה.",
      ),
      figure: triangleFigure(base, height, locale),
    };
  },

  "geometry.angle-sum"(rng, locale) {
    const a = randInt(rng, 20, 80);
    const b = randInt(rng, 20, Math.min(80, 150 - a));
    const third = 180 - a - b;
    return {
      prompt: say(
        locale,
        `A triangle has angles ${a}° and ${b}°. Find the third angle.`,
        `במשולש יש זוויות של ${a}° ו-${b}°. מצאו את הזווית השלישית.`,
      ),
      expectedAnswer: String(third),
      acceptedAnswers: [`${third}°`, `${third} degrees`],
      hints: hint(
        locale,
        "The three angles of a triangle add to a half-turn.",
        "שלוש הזוויות במשולש משלימות חצי סיבוב.",
      ),
      figure: angleFigure(a, b),
    };
  },

  "geometry.pythagorean"(rng, locale) {
    const [a, b, c] = choice(rng, TRIPLES);
    return {
      prompt: say(
        locale,
        `In a right triangle the legs are ${a} and ${b}. Find the hypotenuse.`,
        `במשולש ישר-זווית הניצבים הם ${a} ו-${b}. מצאו את היתר.`,
      ),
      expectedAnswer: String(c),
      acceptedAnswers: [`c = ${c}`, `c=${c}`],
      hints: [
        say(
          locale,
          "Square each leg, add those squares, then take the square root.",
          "העלו כל ניצב בריבוע, חברו, ואז הוציאו שורש.",
        ),
        say(
          locale,
          "The hypotenuse is the side opposite the right angle.",
          "היתר הוא הצלע שמול הזווית הישרה.",
        ),
      ],
      explanation: say(
        locale,
        `Square the legs: ${a}^2 = ${a * a} and ${b}^2 = ${b * b}. Add them: ${a * a + b * b}. The square root is ${c}.`,
        `מרבעים את הניצבים: ${a}^2 = ${a * a} ו-${b}^2 = ${b * b}. מחברים: ${a * a + b * b}. השורש הוא ${c}.`,
      ),
      figure: rightTriangleFigure(a, b),
    };
  },

  "geometry.circle-circumference"(rng, locale) {
    const radius = randInt(rng, 2, 12);
    const coeff = radius * 2;
    return {
      prompt: say(
        locale,
        `The circle has radius ${radius}. Find the circumference. Leave π in the answer.`,
        `למעגל רדיוס ${radius}. מצאו את ההיקף. השאירו את π בתשובה.`,
      ),
      expectedAnswer: `${coeff}π`,
      acceptedAnswers: [`${coeff}pi`, `C = ${coeff}π`, `C=${coeff}π`],
      hints: [
        say(
          locale,
          "The circumference is two pi times the radius.",
          "ההיקף הוא שתי פעמים פאי כפול הרדיוס.",
        ),
        say(
          locale,
          "Leave pi in the answer rather than a decimal.",
          "השאירו את פאי בתשובה במקום מספר עשרוני.",
        ),
      ],
      explanation: say(
        locale,
        `Circumference is 2 * pi * r. Here r = ${radius}, so 2 * ${radius} * pi = ${coeff}π.`,
        `ההיקף הוא 2 * pi * r. כאן r = ${radius}, לכן 2 * ${radius} * pi = ${coeff}π.`,
      ),
      figure: circleFigure(radius, locale),
    };
  },

  "geometry.circle-area"(rng, locale) {
    const radius = randInt(rng, 2, 12);
    const coeff = radius * radius;
    return {
      prompt: say(
        locale,
        `The circle has radius ${radius}. Find the area. Leave π in the answer.`,
        `למעגל רדיוס ${radius}. מצאו את השטח. השאירו את π בתשובה.`,
      ),
      expectedAnswer: `${coeff}π`,
      acceptedAnswers: [`${coeff}pi`, `A = ${coeff}π`, `A=${coeff}π`],
      hints: [
        say(
          locale,
          "The area is pi times the radius squared.",
          "השטח הוא פאי כפול הרדיוס בריבוע.",
        ),
        say(
          locale,
          "Square the radius before multiplying by pi.",
          "רבעו את הרדיוס לפני הכפל בפאי.",
        ),
      ],
      explanation: say(
        locale,
        `Area is pi * r^2. ${radius}^2 = ${coeff}, so the area is ${coeff}π.`,
        `השטח הוא pi * r^2. ${radius}^2 = ${coeff}, לכן השטח הוא ${coeff}π.`,
      ),
      figure: circleFigure(radius, locale),
    };
  },

  "geometry.similar-triangles"(rng, locale) {
    const scale = choice(rng, [2, 3]);
    const small = randInt(rng, 2, 6);
    const known = randInt(rng, 2, 6);
    const big = small * scale;
    const missing = known * scale;
    return {
      prompt: say(
        locale,
        `The triangles are similar. Matching sides ${small} and ${big}, and ${known} and x. Find x.`,
        `המשולשים דומים. צלעות מתאימות ${small} ו-${big}, ו-${known} ו-x. מצאו את x.`,
      ),
      expectedAnswer: String(missing),
      acceptedAnswers: [`x = ${missing}`, `x=${missing}`],
      hints: [
        say(
          locale,
          "Matching sides of similar triangles stay in the same ratio.",
          "צלעות מתאימות במשולשים דומים שומרות על אותו יחס.",
        ),
        say(
          locale,
          "The larger triangle is a whole-number copy of the smaller one.",
          "המשולש הגדול הוא עותק שלם של המשולש הקטן.",
        ),
      ],
      explanation: say(
        locale,
        `The matching sides ${small} and ${big} give scale factor ${scale}. Multiply the other known side: ${known} * ${scale} = ${missing}.`,
        `הצלעות המתאימות ${small} ו-${big} נותנות מקדם ${scale}. כופלים את הצלע הידועה השנייה: ${known} * ${scale} = ${missing}.`,
      ),
      figure: similarFigure(small, big, known),
    };
  },

  "stats.mean"(rng, locale) {
    const count = 5;
    const values = dataSet(rng, count, 1, 12);
    const sum = values.reduce((total, value) => total + value, 0);
    const mean = sum / count;
    const expected = Number.isInteger(mean) ? String(mean) : mean.toFixed(1);
    return {
      prompt: say(
        locale,
        `Find the mean of the data set:\n${values.join(",  ")}`,
        `מצאו את הממוצע של קבוצת הנתונים:\n${values.join(",  ")}`,
      ),
      expectedAnswer: expected,
      acceptedAnswers: Number.isInteger(mean) ? [] : [String(mean)],
      hints: hint(
        locale,
        "Add the values, then divide by how many there are.",
        "חברו את הערכים, ואז חלקו במספר הערכים.",
      ),
      figure: dataDotPlotFigure(values),
      figureSteps: dataDotPlotSteps(values),
    };
  },

  "stats.median"(rng, locale) {
    const values = dataSet(rng, 5, 1, 20);
    const ordered = [...values].sort((a, b) => a - b);
    const median = ordered[2];
    return {
      prompt: say(
        locale,
        `Find the median of the data set:\n${values.join(",  ")}`,
        `מצאו את החציון של קבוצת הנתונים:\n${values.join(",  ")}`,
      ),
      expectedAnswer: String(median),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Put the values in order, then take the middle one.",
        "סדרו את הערכים, ואז קחו את האמצעי.",
      ),
      figure: dataDotPlotFigure(values),
      figureSteps: dataDotPlotSteps(values),
    };
  },

  "stats.mode"(rng, locale) {
    const mode = randInt(rng, 1, 9);
    const fillers = [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((n) => n !== mode);
    const once: number[] = [];
    const pool = [...fillers];
    while (once.length < 3 && pool.length) {
      once.push(pool.splice(randInt(rng, 0, pool.length - 1), 1)[0]);
    }
    const values = [mode, mode, mode, once[0], once[1], once[2]];
    for (let i = values.length - 1; i > 0; i -= 1) {
      const j = randInt(rng, 0, i);
      const swap = values[i];
      values[i] = values[j];
      values[j] = swap;
    }
    return {
      prompt: say(
        locale,
        `Find the mode of the data set:\n${values.join(",  ")}`,
        `מצאו את השכיח של קבוצת הנתונים:\n${values.join(",  ")}`,
      ),
      expectedAnswer: String(mode),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "The mode is the value that appears most often.",
        "השכיח הוא הערך שמופיע הכי הרבה פעמים.",
      ),
      figure: dataDotPlotFigure(values),
      figureSteps: dataDotPlotSteps(values),
    };
  },

  "stats.range"(rng, locale) {
    const values = dataSet(rng, 5, 1, 20);
    const span = Math.max(...values) - Math.min(...values);
    return {
      prompt: say(
        locale,
        `Find the range of the data set:\n${values.join(",  ")}`,
        `מצאו את הטווח של קבוצת הנתונים:\n${values.join(",  ")}`,
      ),
      expectedAnswer: String(span),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Subtract the smallest value from the largest.",
        "חסרו את הערך הקטן ביותר מהערך הגדול ביותר.",
      ),
      figure: dataDotPlotFigure(values),
      figureSteps: dataDotPlotSteps(values),
    };
  },

  "stats.sample-space"(rng, locale) {
    const face = randInt(rng, 1, 6);
    const events = [
      {
        en: "even",
        he: "זוגי",
        ...fraction(3, 6),
      },
      {
        en: "odd",
        he: "אי-זוגי",
        ...fraction(3, 6),
      },
      {
        en: "greater than 4",
        he: "גדול מ-4",
        ...fraction(2, 6),
      },
      {
        en: "less than 3",
        he: "קטן מ-3",
        ...fraction(2, 6),
      },
      {
        en: String(face),
        he: String(face),
        ...fraction(1, 6),
      },
    ];
    const event = choice(rng, events);
    return {
      prompt: say(
        locale,
        `A fair six-sided die is rolled. Find P(${event.en}).`,
        `מטילים קובייה הוגנת. מצאו את P(${event.he}).`,
      ),
      expectedAnswer: event.expectedAnswer,
      acceptedAnswers: event.acceptedAnswers,
      hints: hint(
        locale,
        "Write the favorable faces over the number of faces.",
        "כתבו את מספר הפאות המתאימות חלקי מספר הפאות.",
      ),
      figure: [],
    };
  },

  "stats.independent-events"(rng, locale) {
    const scenes = [
      {
        en: "A fair coin is tossed twice. Find P(heads, heads).",
        he: "מטבע הוגן מוטל פעמיים. מצאו את P(עץ, עץ).",
        ...fraction(1, 4),
        explainEn: "The tosses are independent, and each has probability 1/2. Multiply: (1/2) * (1/2) = 1/4.",
        explainHe: "ההטלות בלתי תלויות, ולכל אחת הסתברות 1/2. כופלים: (1/2) * (1/2) = 1/4.",
      },
      {
        en: "A fair coin is tossed, then a fair die is rolled. Find P(heads and even).",
        he: "מטילים מטבע הוגן ואז קובייה הוגנת. מצאו את P(עץ וזוגי).",
        ...fraction(1, 4),
        explainEn: "P(heads) = 1/2 and P(even) = 3/6 = 1/2. Independent events multiply: (1/2) * (1/2) = 1/4.",
        explainHe: "P(עץ) = 1/2 ו-P(זוגי) = 3/6 = 1/2. מאורעות בלתי תלויים נכפלים: (1/2) * (1/2) = 1/4.",
      },
      {
        en: "A fair die is rolled twice. Find P(even, even).",
        he: "מטילים קובייה הוגנת פעמיים. מצאו את P(זוגי, זוגי).",
        ...fraction(1, 4),
        explainEn: "Each roll is even with probability 1/2. The rolls are independent, so (1/2) * (1/2) = 1/4.",
        explainHe: "כל הטלה זוגית בהסתברות 1/2. ההטלות בלתי תלויות, לכן (1/2) * (1/2) = 1/4.",
      },
      {
        en: "A fair coin is tossed, then a fair die is rolled. Find P(heads and 6).",
        he: "מטילים מטבע הוגן ואז קובייה הוגנת. מצאו את P(עץ ו-6).",
        ...fraction(1, 12),
        explainEn: "P(heads) = 1/2 and P(6) = 1/6. Independent events multiply: (1/2) * (1/6) = 1/12.",
        explainHe: "P(עץ) = 1/2 ו-P(6) = 1/6. מאורעות בלתי תלויים נכפלים: (1/2) * (1/6) = 1/12.",
      },
    ];
    const scene = choice(rng, scenes);
    return {
      prompt: say(locale, scene.en, scene.he),
      expectedAnswer: scene.expectedAnswer,
      acceptedAnswers: scene.acceptedAnswers,
      hints: [
        say(
          locale,
          "For independent events, multiply the separate probabilities.",
          "במאורעות בלתי תלויים כופלים את ההסתברויות הנפרדות.",
        ),
        say(
          locale,
          "Each outcome of the first action pairs with each outcome of the second.",
          "כל תוצאה של הפעולה הראשונה מצטרפת לכל תוצאה של הפעולה השנייה.",
        ),
      ],
      explanation: say(locale, scene.explainEn, scene.explainHe),
      figure: [],
    };
  },
};
